/**
 * Sprint 7 — Feature visibility hook.
 *
 * Fetches `/api/features/visibility` once on mount and exposes the resolved
 * booleans for contextual features (Recipes, Transfer Stock, Multi-branch
 * Attendance). Backend is authoritative; this hook only drives UX.
 *
 * Fail-closed: on fetch error we treat all contextual features as HIDDEN so
 * we never accidentally expose a feature when the resolver is unreachable.
 */
import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

const DEFAULT = {
  loading: true,
  error: false,
  features: {
    recipes: { visible: false },
    transfer_stock: { visible: false },
    multi_branch_attendance: { visible: false },
  },
  workspace_feature_overrides: {},
  plan_id: "",
  industry: "",
  refresh: () => {},
};

const FeatureVisibilityContext = createContext(DEFAULT);

export function FeatureVisibilityProvider({ children }) {
  const { user, workspace } = useAuth();
  const [state, setState] = useState(DEFAULT);

  const load = useCallback(async () => {
    if (!user || !workspace?.workspace_id) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }
    try {
      const { data } = await api.get("/features/visibility");
      setState({
        loading: false,
        error: false,
        features: data.features || DEFAULT.features,
        workspace_feature_overrides: data.workspace_feature_overrides || {},
        plan_id: data.plan_id || "",
        industry: data.industry || "",
        refresh: load,
      });
    } catch {
      // Fail closed — treat all contextual features as hidden.
      setState({
        ...DEFAULT,
        loading: false,
        error: true,
        refresh: load,
      });
    }
  }, [user, workspace?.workspace_id]);

  useEffect(() => { load(); }, [load]);

  return (
    <FeatureVisibilityContext.Provider value={{ ...state, refresh: load }}>
      {children}
    </FeatureVisibilityContext.Provider>
  );
}

export function useFeatureVisibility() {
  return useContext(FeatureVisibilityContext);
}

export function useFeatureVisible(key) {
  const { features, loading } = useFeatureVisibility();
  if (loading) return null; // null = unknown; caller may render skeleton
  return !!features?.[key]?.visible;
}
